import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, ImageOff, Film, ChevronDown, ChevronUp } from "lucide-react";
import { api, resolveImg, formatApiError } from "@/lib/api";
import { downloadCsv } from "@/lib/utils";
import { Field, TextArea, AdminButton } from "@/pages/admin/ui";

const fmt = (d) => (d ? new Date(d).toLocaleString("en-IN") : "");
const STATUSES = ["Draft", "Paid", "Submitted", "Shortlisted", "Interview Scheduled", "Interview Completed", "Selected", "Not Selected"];
const selCls = "border border-ptf-primary/20 bg-white px-2 py-1.5 text-xs outline-none focus:border-ptf-secondary text-ptf-text";

function SettingsForm() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["dawn-settings"], queryFn: async () => (await api.get("/admin/dawn/settings")).data });
  const [f, setF] = useState(null);
  const current = f || data || {};
  const mut = useMutation({
    mutationFn: (body) => api.put("/admin/dawn/settings", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["dawn-settings"] }); toast.success("DAWN settings saved"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const save = (e) => { e.preventDefault(); mut.mutate({ application_fee: Number(current.application_fee ?? 499), open: current.open !== false }); };
  if (!data && !f) return <Loader2 className="h-5 w-5 animate-spin text-ptf-primary" />;
  return (
    <form onSubmit={save} className="space-y-4 max-w-md">
      <Field label="Application Fee (INR)" type="number" value={current.application_fee ?? 499} onChange={(e) => setF({ ...current, application_fee: e.target.value })} data-testid="dawn-settings-fee" />
      <label className="flex items-center gap-3 cursor-pointer">
        <input type="checkbox" checked={current.open !== false} onChange={(e) => setF({ ...current, open: e.target.checked })} data-testid="dawn-settings-open" className="h-4 w-4 accent-ptf-secondary" />
        <span className="text-sm font-medium text-ptf-primary">Applications open</span>
      </label>
      <AdminButton type="submit" data-testid="dawn-settings-save">Save Settings</AdminButton>
    </form>
  );
}

function MediaThumb({ appId, kind, item }) {
  const [broken, setBroken] = useState(false);
  if (!item?.id) return null;
  const src = resolveImg(`/api/admin/dawn/media/${appId}/${kind}/${item.id}`);
  return (
    <div className="border border-ptf-primary/10 bg-white" data-testid={`dawn-admin-media-${kind}-${item.id}`}>
      {broken ? (
        <div className="h-24 w-full grid place-items-center text-ptf-text/30"><ImageOff className="h-5 w-5" /></div>
      ) : kind === "belief_video" ? (
        <video src={src} controls className="h-24 w-full bg-black" />
      ) : (
        <img src={src} alt="" onError={() => setBroken(true)} className="h-24 w-full object-cover" />
      )}
    </div>
  );
}

function ApplicationRow({ app }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(app.reviewer_notes || "");
  const updateStatus = useMutation({
    mutationFn: (status) => api.put(`/admin/dawn/applications/${app.id}/status`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["dawn-admin-applications"] }); toast.success("Status updated"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const saveNotes = useMutation({
    mutationFn: () => api.put(`/admin/dawn/applications/${app.id}/status`, { reviewer_notes: notes }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["dawn-admin-applications"] }); toast.success("Reviewer notes saved"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <div className="border border-ptf-primary/10 bg-white" data-testid={`dawn-admin-app-${app.id}`}>
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`dawn-admin-app-toggle-${app.id}`} className="grid place-items-center h-8 w-8 border border-ptf-primary/15 hover:bg-ptf-primary/5">
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        <div className="min-w-[160px]">
          <p className="font-medium text-ptf-primary text-sm">{app.full_name || "(unnamed)"}</p>
          <p className="text-[0.6rem] uppercase tracking-[0.1em] text-ptf-text/50">{app.email}</p>
        </div>
        <span className="text-xs text-ptf-text/60">{app.application_number || "-"}</span>
        <span className="text-xs text-ptf-text/60">{app.city || "-"}</span>
        <span className={`text-[0.6rem] uppercase tracking-[0.1em] px-2 py-1 ${app.payment_status === "paid" ? "bg-emerald-600/10 text-emerald-700" : "bg-ptf-primary/5 text-ptf-text/50"}`}>
          {app.payment_status === "paid" ? `Paid ₹${Number(app.amount || 0).toLocaleString("en-IN")}` : "Unpaid"}
        </span>
        <span className="text-xs text-ptf-text/45">{fmt(app.submitted_at || app.created_at)}</span>
        <select
          value={app.status} onChange={(e) => updateStatus.mutate(e.target.value)} disabled={updateStatus.isPending}
          data-testid={`dawn-admin-app-status-${app.id}`} className={`${selCls} ml-auto`}
        >
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      {open && (
        <div className="px-4 pb-4 border-t border-ptf-primary/10 pt-4 space-y-4" data-testid={`dawn-admin-app-detail-${app.id}`}>
          <div className="grid sm:grid-cols-2 gap-3 text-xs text-ptf-text/70">
            <p><strong>Mobile:</strong> {app.mobile || "-"}</p>
            <p><strong>Date of Birth:</strong> {app.dob || "-"}</p>
            <p><strong>Gender:</strong> {app.gender || "-"}</p>
            <p><strong>Education:</strong> {app.education || "-"}</p>
            <p><strong>Instagram:</strong> {app.instagram || "-"}</p>
            <p><strong>LinkedIn:</strong> {app.linkedin || "-"}</p>
            <p><strong>Facebook:</strong> {app.facebook || "-"}</p>
            <p><strong>Order / Payment ID:</strong> {app.order_id || "-"} / {app.payment_id || "-"}</p>
          </div>
          <div className="bg-ptf-primary/[0.03] border border-ptf-primary/10 px-3 py-2" data-testid={`dawn-admin-terms-record-${app.id}`}>
            <p className="text-[0.62rem] uppercase tracking-[0.14em] text-ptf-primary/60 mb-1">Signed Acceptance Record</p>
            {app.terms_accepted_at ? (
              <p className="text-xs text-ptf-text/70">
                Terms &amp; Conditions <strong>v{app.terms_version || "1"}</strong> accepted on {fmt(app.terms_accepted_at)}
                {app.terms_accept_ip ? ` from IP ${app.terms_accept_ip}` : ""}
              </p>
            ) : (
              <p className="text-xs text-ptf-text/45">Not yet accepted.</p>
            )}
          </div>
          {app.work_experience && <p className="text-xs text-ptf-text/70"><strong>Work Experience:</strong> {app.work_experience}</p>}
          {app.about_you && <p className="text-xs text-ptf-text/70"><strong>About You:</strong> {app.about_you}</p>}
          {(app.answers ? Object.entries(app.answers) : []).map(([k, v]) => v && (
            <p key={k} className="text-xs text-ptf-text/70"><strong>{k}:</strong> {v}</p>
          ))}
          {app.one_word && <p className="text-xs text-ptf-text/70"><strong>One Word:</strong> {app.one_word} ({app.one_word_why})</p>}
          {app.why_you && <p className="text-xs text-ptf-text/70"><strong>Why You:</strong> {app.why_you}</p>}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            <MediaThumb appId={app.id} kind="profile_photo" item={app.profile_photo} />
            <MediaThumb appId={app.id} kind="belief_video" item={app.belief_video} />
          </div>
          {app.drive_folder_url && (
            <a href={app.drive_folder_url} target="_blank" rel="noopener noreferrer" data-testid={`dawn-admin-drive-link-${app.id}`} className="inline-flex items-center gap-1.5 text-xs underline text-ptf-secondary hover:text-ptf-primary">
              <Film className="h-3.5 w-3.5" /> View application PDF &amp; video backup in Google Drive
            </a>
          )}
          <div>
            <p className="text-[0.62rem] uppercase tracking-[0.14em] text-ptf-primary/60 mb-1">Reviewer Notes (private)</p>
            <TextArea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`dawn-admin-notes-${app.id}`} />
            <AdminButton type="button" variant="secondary" className="mt-2" onClick={() => saveNotes.mutate()} data-testid={`dawn-admin-notes-save-${app.id}`}>Save Notes</AdminButton>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DawnAdmin() {
  const { data: apps = [], isLoading } = useQuery({ queryKey: ["dawn-admin-applications"], queryFn: async () => (await api.get("/admin/dawn/applications")).data });
  const [filter, setFilter] = useState("");
  const filtered = filter ? apps.filter((a) => a.status === filter) : apps;
  const paidCount = apps.filter((a) => a.payment_status === "paid").length;
  const selectedCount = apps.filter((a) => a.status === "Selected").length;

  const exportCsv = () => {
    downloadCsv(
      [["Name", "Email", "Mobile", "City", "Status", "Payment Status", "Amount", "Application Number", "Submitted At"],
        ...filtered.map((a) => [a.full_name, a.email, a.mobile, a.city, a.status, a.payment_status, a.amount, a.application_number, a.submitted_at])],
      "dawn-applications.csv"
    );
  };

  return (
    <div className="space-y-12">
      <div>
        <h2 className="font-display uppercase text-2xl tracking-tight text-ptf-primary mb-5">DAWN Settings</h2>
        <SettingsForm />
      </div>
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <h2 className="font-display uppercase text-2xl tracking-tight text-ptf-primary">
            Applications ({apps.length}) &middot; {paidCount} Paid &middot; {selectedCount}/24 Selected
          </h2>
          <div className="flex items-center gap-2">
            <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="dawn-admin-filter-status" className={selCls}>
              <option value="">All statuses</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <AdminButton type="button" variant="secondary" onClick={exportCsv} data-testid="dawn-admin-export-csv">Export CSV</AdminButton>
          </div>
        </div>
        {isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin text-ptf-primary" />
        ) : filtered.length === 0 ? (
          <p className="text-sm text-ptf-text/50 italic font-serif-ed">No applications yet.</p>
        ) : (
          <div className="space-y-2">
            {filtered.map((a) => <ApplicationRow key={a.id} app={a} />)}
          </div>
        )}
      </div>
    </div>
  );
}
